import { useCallback, useMemo, useState } from 'react';
import { MdClose, MdOutlineFileDownload } from 'react-icons/md';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { EmptyState } from '@Components/EmptyState';
import { IconButton } from '@Components/IconButton';
import { EMPTY_DATE_RANGE, FILTER_ALL } from '../alexa-visibility-constants';
import {
  getProductPositionStats,
  getQueryCitationStatsList,
  getResponseFormats,
  getTargetCitationSummary,
  rankQueriesByVisibility
} from '../alexa-visibility-citation-utils';
import { filterResultsByDateRange } from '../alexa-visibility-utils';
import { useBrandColor } from '../hooks/useBrandColor';
import { usePrintView } from '../hooks/usePrintView';
import { CitationQueryCard } from './citation-comparison/CitationQueryCard';
import { CitationSummaryCards } from './citation-comparison/CitationSummaryCards';

interface CitationComparisonTabProps {
  // The page's results, already narrowed by the page-level date filter.
  results: AlexaVisibilityResult[];
  // Every result, before any date filter. The tab's own date filter starts from these, so it
  // overrides the page-level one.
  allResults: AlexaVisibilityResult[];
  targetBrand: string;
}

// The browser's print dialog is landscape here: the three summary cards and the stat boxes need
// the width. Added only while the print view is open so other pages' printing is unaffected.
const PRINT_PAGE_STYLE = '@page { size: landscape; margin: 0.5in; }';

export const CitationComparisonTab = ({
  results,
  allResults,
  targetBrand
}: CitationComparisonTabProps) => {
  const [queryFilter, setQueryFilter] = useState(FILTER_ALL);
  const [formatFilter, setFormatFilter] = useState(FILTER_ALL);
  const [dateRange, setDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [isPrinting, setIsPrinting] = useState(false);

  const hasTabDateRange = Boolean(dateRange.start || dateRange.end);
  // The tab's own date filter replaces the page's when it is set.
  const dateScoped = useMemo(
    () =>
      hasTabDateRange
        ? filterResultsByDateRange(allResults, dateRange.start, dateRange.end)
        : results,
    [allResults, dateRange, hasTabDateRange, results]
  );

  const formatOptions = useMemo(() => getResponseFormats(dateScoped), [dateScoped]);
  const activeFormat = formatOptions.includes(formatFilter) ? formatFilter : FILTER_ALL;
  const formatScoped = useMemo(
    () =>
      activeFormat === FILTER_ALL
        ? dateScoped
        : dateScoped.filter(
            result => result.classification?.responseFormat?.trim() === activeFormat
          ),
    [activeFormat, dateScoped]
  );

  const allQueryStats = useMemo(
    () => getQueryCitationStatsList(formatScoped, targetBrand),
    [formatScoped, targetBrand]
  );
  const activeQuery = allQueryStats.some(stat => stat.query === queryFilter)
    ? queryFilter
    : FILTER_ALL;
  const isSingleQuery = activeQuery !== FILTER_ALL;
  const visibleStats = useMemo(
    () =>
      isSingleQuery ? allQueryStats.filter(stat => stat.query === activeQuery) : allQueryStats,
    [activeQuery, allQueryStats, isSingleQuery]
  );
  const scopedResults = useMemo(() => visibleStats.flatMap(stat => stat.results), [visibleStats]);

  const summary = useMemo(() => getTargetCitationSummary(scopedResults), [scopedResults]);
  const products = useMemo(() => getProductPositionStats(scopedResults), [scopedResults]);
  const topQueries = useMemo(() => rankQueriesByVisibility(allQueryStats), [allQueryStats]);
  const getBrandColor = useBrandColor(allResults, targetBrand);

  const closePrint = useCallback(() => setIsPrinting(false), []);
  usePrintView(isPrinting, closePrint, PRINT_PAGE_STYLE);

  const toggle = (query: string) =>
    setExpanded(previous => {
      const next = new Set(previous);
      if (!next.delete(query)) {
        next.add(query);
      }
      return next;
    });

  if (allResults.length === 0) {
    return (
      <EmptyState
        title='No results yet'
        subtitle='Query-by-query citation comparisons will appear here once a run completes.'
      />
    );
  }

  const scopeLabel = isSingleQuery ? 'this query · all runs' : 'all queries · all runs';

  return (
    <div className='citation-comparison-tab'>
      <div className='aars-tab-actions'>
        <div className='aars-tab-header'>
          <div className='aars-tab-title'>Citation comparison</div>
          <div className='aars-tab-description'>
            Same query across multiple runs — spot where Alexa for Shopping responses vary and track{' '}
            {targetBrand}&apos;s consistency.
          </div>
        </div>
        <div className='aars-export-buttons no-print'>
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdOutlineFileDownload />}
            onClick={() => setIsPrinting(true)}
          >
            PDF
          </Button>
        </div>
      </div>

      <div className='aars-cc-filters no-print'>
        <Dropdown
          size={Size.LG}
          searchable
          value={activeQuery}
          options={[
            { label: 'All queries', value: FILTER_ALL },
            ...allQueryStats.map(stat => ({ label: stat.query, value: stat.query }))
          ]}
          onChange={value => setQueryFilter(value ?? FILTER_ALL)}
        />
        <Dropdown
          size={Size.LG}
          value={activeFormat}
          options={[
            { label: 'All response types', value: FILTER_ALL },
            ...formatOptions.map(format => ({ label: format, value: format }))
          ]}
          onChange={value => setFormatFilter(value ?? FILTER_ALL)}
        />
        <div className='aars-date-filter'>
          <DatePicker
            range={hasTabDateRange ? dateRange : undefined}
            placeholder='All dates'
            onChange={({ start, end }) => setDateRange({ start, end })}
          />
          {hasTabDateRange && (
            <IconButton
              appearance={Appearance.EMPTY}
              aria-label='Clear date filter'
              onClick={() => setDateRange(EMPTY_DATE_RANGE)}
            >
              <MdClose />
            </IconButton>
          )}
        </div>
        <span className='aars-cc-filter-count'>
          {visibleStats.length} of {allQueryStats.length} queries
        </span>
      </div>

      {allQueryStats.length === 0 ? (
        <EmptyState
          title='No results match these filters'
          subtitle='Choose different filters to see citation comparisons.'
        />
      ) : (
        <>
          <CitationSummaryCards
            targetBrand={targetBrand}
            scopeLabel={scopeLabel}
            summary={summary}
            products={products}
            topQueries={topQueries}
            isSingleQuery={isSingleQuery}
          />
          <div className='aars-cc-query-list'>
            {visibleStats.map((stats, index) => (
              <CitationQueryCard
                key={stats.query}
                stats={stats}
                index={index}
                targetBrand={targetBrand}
                isExpanded={isSingleQuery || expanded.has(stats.query)}
                onToggle={() => toggle(stats.query)}
                getBrandColor={getBrandColor}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
